import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import { webRtcIceServerUrl } from '../../webrtcConfig.ts';
import {
  errorText,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

export { webRtcIceServerUrl };

interface ParsedCandidate {
  foundation: string;
  component: number;
  protocol: string;
  priority: number;
  address: string;
  port: number;
  type: string;
  relatedAddress: string | null;
  relatedPort: number | null;
  tcpType: string | null;
  networkCost: number | null;
  generation: number | null;
  usernameFragment: string | null;
}

interface CandidateRecord {
  candidate: string;
  sdpMid: string | null;
  sdpMLineIndex: number | null;
  usernameFragment: string | null;
  url?: string | null;
}

type CandidateStatus = 'valid' | 'invalid';

function tokenValue(parts: readonly string[], name: string): string | null {
  const index = parts.indexOf(name);
  return index >= 0 && index + 1 < parts.length
    ? (parts[index + 1] ?? null)
    : null;
}

function numberToken(value: string | null): number | null {
  if (value == null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseIceCandidate(candidate: string): ParsedCandidate | null {
  const parts = candidate.trim().replace(/^a=/, '').split(/\s+/);
  if (parts.length < 8 || !parts[0]?.startsWith('candidate:')) {
    return null;
  }
  const component = Number(parts[1]);
  const priority = Number(parts[3]);
  const port = Number(parts[5]);
  if (
    !Number.isInteger(component) ||
    !Number.isInteger(priority) ||
    !Number.isInteger(port) ||
    parts[6] != 'typ'
  ) {
    return null;
  }
  return {
    foundation: parts[0].slice('candidate:'.length),
    component,
    protocol: String(parts[2]).toLowerCase(),
    priority,
    address: String(parts[4]),
    port,
    type: String(parts[7]),
    relatedAddress: tokenValue(parts, 'raddr'),
    relatedPort: numberToken(tokenValue(parts, 'rport')),
    tcpType: tokenValue(parts, 'tcptype'),
    networkCost: numberToken(tokenValue(parts, 'network-cost')),
    generation: numberToken(tokenValue(parts, 'generation')),
    usernameFragment: tokenValue(parts, 'ufrag'),
  };
}

function isIpv4(value: string): boolean {
  const parts = value.split('.');
  return (
    parts.length == 4 &&
    parts.every((part) => {
      if (!/^\d{1,3}$/.test(part)) {
        return false;
      }
      const octet = Number(part);
      return octet >= 0 && octet <= 255 && String(octet) == part;
    })
  );
}

function isIpv6(value: string): boolean {
  if (!/^[0-9A-Fa-f:.]+$/.test(value) || !value.includes(':')) {
    return false;
  }
  const doubleColonParts = value.split('::');
  if (doubleColonParts.length > 2) {
    return false;
  }
  const groups = value.split(':').filter(Boolean);
  if (groups.some((group) => !/^[0-9A-Fa-f]{1,4}$/.test(group))) {
    return false;
  }
  return doubleColonParts.length == 2 ? groups.length < 8 : groups.length == 8;
}

function isMdnsAddress(value: string): boolean {
  return (
    value.length <= 253 &&
    /^(?=.{1,253}$)(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+local$/i.test(
      value,
    )
  );
}

function isCandidateAddress(value: string): boolean {
  return isIpv4(value) || isIpv6(value) || isMdnsAddress(value);
}

function isPublicIp(value: string): boolean {
  if (!isIpv4(value) && !value.includes(':')) {
    return false;
  }
  if (/^(?:10|127)\./.test(value)) {
    return false;
  }
  if (/^192\.168\./.test(value)) {
    return false;
  }
  if (/^172\.(?:1[6-9]|2\d|3[01])\./.test(value)) {
    return false;
  }
  if (/^(?:0\.0\.0\.0|::1|fc|fd|fe80)/i.test(value)) {
    return false;
  }
  return true;
}

function candidateRecord(value: unknown): CandidateRecord | null {
  if (!isRecord(value)) {
    return null;
  }
  if (
    typeof value.candidate != 'string' ||
    (value.url != null && typeof value.url != 'string') ||
    (value.sdpMid != null && typeof value.sdpMid != 'string') ||
    (value.sdpMLineIndex != null && typeof value.sdpMLineIndex != 'number') ||
    (value.usernameFragment != null &&
      typeof value.usernameFragment != 'string')
  ) {
    return null;
  }
  return value as unknown as CandidateRecord;
}

function candidateError(value: unknown): string | null {
  const record = candidateRecord(value);
  if (!record) {
    return 'candidate structure or field types are invalid';
  }
  const candidate = parseIceCandidate(record.candidate);
  if (!candidate) {
    return 'candidate string cannot be parsed';
  }
  if (
    candidate.foundation.length < 1 ||
    candidate.foundation.length > 32 ||
    !/^[+/0-9A-Za-z]+$/.test(candidate.foundation)
  ) {
    return 'foundation must contain 1 to 32 ICE characters';
  }
  if (
    !Number.isInteger(candidate.component) ||
    candidate.component < 1 ||
    candidate.component > 256
  ) {
    return 'component must be an integer from 1 to 256';
  }
  if (
    !Number.isInteger(candidate.priority) ||
    candidate.priority < 0 ||
    candidate.priority > 0xffffffff
  ) {
    return 'priority must be an unsigned 32-bit integer';
  }
  if (candidate.protocol != 'udp' && candidate.protocol != 'tcp') {
    return 'protocol must be UDP or TCP';
  }
  if (!['host', 'srflx', 'prflx', 'relay'].includes(candidate.type)) {
    return 'candidate type must be host, srflx, prflx, or relay';
  }
  if (
    (candidate.protocol == 'tcp' &&
      !['active', 'passive', 'so'].includes(candidate.tcpType ?? '')) ||
    (candidate.protocol == 'udp' && candidate.tcpType != null)
  ) {
    return 'TCP type is inconsistent with the candidate protocol';
  }
  const activeTcpPort =
    candidate.protocol == 'tcp' &&
    candidate.tcpType == 'active' &&
    candidate.port == 0;
  if (
    !Number.isInteger(candidate.port) ||
    candidate.port > 65535 ||
    (candidate.port < 1 && !activeTcpPort)
  ) {
    return 'port must be from 1 to 65535, or 0 for active TCP';
  }
  if (!isCandidateAddress(candidate.address)) {
    return 'address must be a valid IPv4, IPv6, or mDNS address';
  }
  const derivedCandidate =
    candidate.type == 'srflx' ||
    candidate.type == 'prflx' ||
    candidate.type == 'relay';
  if (
    derivedCandidate &&
    (typeof candidate.relatedAddress != 'string' ||
      (candidate.relatedAddress != '0.0.0.0' &&
        candidate.relatedAddress != '::' &&
        !isCandidateAddress(candidate.relatedAddress)))
  ) {
    return 'derived candidate related address is invalid';
  }
  if (!derivedCandidate && candidate.relatedAddress != null) {
    return 'host candidate must not have a related address';
  }
  if (
    derivedCandidate &&
    (candidate.relatedPort == null ||
      !Number.isInteger(candidate.relatedPort) ||
      candidate.relatedPort < 0 ||
      candidate.relatedPort > 65535)
  ) {
    return 'derived candidate related port must be from 0 to 65535';
  }
  if (!derivedCandidate && candidate.relatedPort != null) {
    return 'host candidate must not have a related port';
  }
  if (
    candidate.networkCost != null &&
    (!Number.isInteger(candidate.networkCost) ||
      candidate.networkCost < 0 ||
      candidate.networkCost > 999)
  ) {
    return 'network cost must be an integer from 0 to 999';
  }
  if (
    candidate.generation != null &&
    (!Number.isInteger(candidate.generation) ||
      candidate.generation < 0 ||
      candidate.generation > 0xffffffff)
  ) {
    return 'generation must be an unsigned 32-bit integer';
  }
  const usernameFragment =
    candidate.usernameFragment ?? record.usernameFragment ?? null;
  if (
    usernameFragment != null &&
    !/^[+/0-9A-Za-z]{4,256}$/.test(usernameFragment)
  ) {
    return 'username fragment must contain 4 to 256 ICE characters';
  }
  if (record.sdpMid == null && record.sdpMLineIndex == null) {
    return 'SDP mid and m-line index cannot both be null';
  }
  if (
    record.sdpMLineIndex != null &&
    (!Number.isInteger(record.sdpMLineIndex) ||
      record.sdpMLineIndex < 0 ||
      record.sdpMLineIndex > 65535)
  ) {
    return 'SDP m-line index must be an unsigned 16-bit integer';
  }
  if (
    record.usernameFragment != null &&
    candidate.usernameFragment != null &&
    record.usernameFragment != candidate.usernameFragment
  ) {
    return 'raw and parsed username fragments do not match';
  }
  if (
    record.url != null &&
    record.url != '' &&
    !/^(?:stun|stuns|turn|turns):/i.test(record.url)
  ) {
    return 'ICE server URL has an invalid scheme';
  }
  return null;
}

export function isInvalidCandidate(value: unknown): boolean {
  return candidateError(value) != null;
}

function candidateRecords(rawData: RawData): CandidateRecord[] {
  if (!Array.isArray(rawData.candidates)) {
    return [];
  }
  return rawData.candidates.flatMap((value) => candidateRecord(value) ?? []);
}

function publicCandidateRecords(rawData: RawData): CandidateRecord[] {
  const candidates = candidateRecords(rawData);
  return candidates.filter((record) => {
    const candidate = parseIceCandidate(record.candidate);
    return (
      candidate != null &&
      (candidate.type == 'srflx' || candidate.type == 'relay') &&
      isPublicIp(candidate.address)
    );
  });
}

export function candidateStatus(rawData: RawData): CandidateStatus {
  const values = Array.isArray(rawData.candidates) ? rawData.candidates : [];
  if (
    values.length == 0 ||
    values.some(isInvalidCandidate) ||
    publicCandidateRecords(rawData).length == 0
  ) {
    return 'invalid';
  }
  return 'valid';
}

async function waitForIceGathering(
  connection: RTCPeerConnection,
): Promise<void> {
  if (connection.iceGatheringState == 'complete') {
    return;
  }
  await new Promise<void>((resolve) => {
    const timeout = window.setTimeout(() => {
      resolve();
    }, 8000);
    connection.addEventListener('icegatheringstatechange', () => {
      if (connection.iceGatheringState == 'complete') {
        window.clearTimeout(timeout);
        resolve();
      }
    });
  });
}

export async function collectWebRtc(): Promise<RawData> {
  const connection = new RTCPeerConnection({
    iceServers: [{ urls: webRtcIceServerUrl }],
  });
  const candidates: CandidateRecord[] = [];
  const errors: RawData[] = [];
  try {
    connection.createDataChannel('abc-test-webrtc');
    connection.addEventListener('icecandidate', (event) => {
      if (!event.candidate) {
        return;
      }
      const candidateWithUrl = event.candidate as RTCIceCandidate & {
        url: string | null;
      };
      candidates.push({
        ...event.candidate.toJSON(),
        candidate: event.candidate.candidate,
        sdpMid: event.candidate.sdpMid,
        sdpMLineIndex: event.candidate.sdpMLineIndex,
        usernameFragment: event.candidate.usernameFragment,
        url: candidateWithUrl.url,
      });
    });
    connection.addEventListener('icecandidateerror', (event) => {
      errors.push({
        address: event.address || null,
        port: event.port ?? null,
        url: event.url,
        errorCode: event.errorCode,
        errorText: event.errorText,
      });
    });
    await connection.setLocalDescription(await connection.createOffer());
    await waitForIceGathering(connection);
    const rawData: RawData = {
      iceGatheringState: connection.iceGatheringState,
      candidates,
      errors,
    };
    return rawData;
  } finally {
    connection.close();
  }
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  const status = candidateStatus(rawData);
  const ips = [
    ...new Set(
      publicCandidateRecords(rawData)
        .map((record) => parseIceCandidate(record.candidate)?.address)
        .filter((value): value is string => typeof value == 'string'),
    ),
  ];
  const ip = ips[0];
  if (ip) {
    if (status == 'invalid') {
      return `invalid ${ip}`;
    }
    return ips.length == 1 ? ip : `${ips.length} public IPs`;
  }
  return 'invalid';
}

async function run(): Promise<TestResult> {
  try {
    return result(await collectWebRtc(), format);
  } catch (error) {
    return result({ error: errorText(error) }, format);
  }
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  const reasons: string[] = [];
  const candidateValues = Array.isArray(rawData.candidates)
    ? rawData.candidates
    : [];
  candidateValues.forEach((candidate, index) => {
    const candidateReason = candidateError(candidate);
    if (candidateReason) {
      reasons.push(`ICE candidate ${index + 1} is invalid: ${candidateReason}`);
    }
  });
  if (candidateValues.length == 0) {
    reasons.push('WebRTC produced no ICE candidates');
  }
  if (
    candidateValues.length > 0 &&
    publicCandidateRecords(rawData).length == 0
  ) {
    reasons.push('WebRTC produced no public ICE candidate');
  }
  return validation(reasons);
}

export const webRtcTest: TestDefinition = {
  id: 'webrtc',
  title: 'WebRTC',
  category: 'variableProfile',
  format,
  run,
  validate,
};
